import { useState, useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useBudgetReco, useSimulateBudget, useMarket } from '../hooks/useSDI'
import { MarketTabsLayout } from '../components/sdi/shared/MarketTabsLayout'
import { KpiCard } from '../components/sdi/shared/KpiCard'
import type { SimulateResponse } from '../hooks/sdi.types'
import {
  DollarSign, TrendingUp, MousePointerClick, Users,
  AlertTriangle, Info, Download, Zap, Target, Save,
  RotateCcw, GitCompare, Edit3, Check,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, ReferenceDot, ReferenceArea,
} from 'recharts'

const fmtEur = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const fmtPct = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1 })
const fmtNum = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
const fmtDec = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 })
const MAX_BUDGET = 50000

interface Scenario { name: string; budget: number; mode: string; result: SimulateResponse; assumptions: Record<string, number> }

function loadScenarios(marketId: string): Scenario[] {
  try { return JSON.parse(localStorage.getItem(`sdi_scenarios_${marketId}`) || '[]') } catch { return [] }
}
function saveScenarios(marketId: string, scenarios: Scenario[]) {
  localStorage.setItem(`sdi_scenarios_${marketId}`, JSON.stringify(scenarios.slice(0, 5)))
}

// ── Editable Value ───────────────────────────────────────────

function EditableValue({ label, value, unit, isModified, onEdit, onReset, tooltip }: {
  label: string; value: string; unit?: string; isModified: boolean; onEdit: (v: number) => void; onReset: () => void; tooltip?: string
}) {
  const [editing, setEditing] = useState(false)
  const [inputVal, setInputVal] = useState(value)

  const handleConfirm = () => {
    const n = parseFloat(inputVal.replace(',', '.'))
    if (!isNaN(n)) onEdit(n)
    setEditing(false)
  }

  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-ink-muted">{label}</span>
        {tooltip && <span title={tooltip}><Info size={10} className="text-ink-faint" /></span>}
      </div>
      <div className="flex items-center gap-1.5">
        {editing ? (
          <div className="flex items-center gap-1">
            <input type="text" value={inputVal} onChange={(e) => setInputVal(e.target.value)}
              className="w-20 text-xs text-right border border-gold rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-gold"
              onKeyDown={(e) => e.key === 'Enter' && handleConfirm()} autoFocus />
            <button onClick={handleConfirm} className="p-0.5 text-brick"><Check size={12} /></button>
          </div>
        ) : (
          <button onClick={() => { setInputVal(value); setEditing(true) }}
            className="flex items-center gap-1 text-xs font-medium text-ink hover:text-brick transition-colors group">
            {value}{unit && <span className="text-ink-faint">{unit}</span>}
            <Edit3 size={10} className="opacity-0 group-hover:opacity-100 text-ink-faint" />
          </button>
        )}
        {isModified && (
          <>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">Modifié</span>
            <button onClick={onReset} className="p-0.5 text-ink-faint hover:text-ink-muted"><RotateCcw size={10} /></button>
          </>
        )}
        {!isModified && <span className="text-[9px] px-1.5 py-0.5 rounded bg-paper-warm text-ink-faint font-medium">Auto</span>}
      </div>
    </div>
  )
}

// ── Save Scenario Modal ──────────────────────────────────────

function SaveScenarioModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (name: string) => void }) {
  const [name, setName] = useState('')
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-sm p-6 animate-slide-up">
        <h3 className="text-lg font-semibold text-ink mb-4">Sauvegarder le scénario</h3>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du scénario"
          className="w-full px-3 py-2 text-sm border border-ink-border rounded-lg focus:outline-none focus:ring-2 focus:ring-gold mb-4" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
          <button onClick={() => { onSave(name || 'Sans nom'); onClose() }} className="px-4 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg">Sauvegarder</button>
        </div>
      </div>
    </div>
  )
}

// ── Scenario Comparison ──────────────────────────────────────

function ScenarioComparison({ scenarios, onClose }: { scenarios: Scenario[]; onClose: () => void }) {
  if (scenarios.length < 2) return null

  return (
    <div className="fixed inset-y-0 right-0 w-[500px] bg-white border-l border-ink-border shadow-elevated z-40 animate-slide-in-right">
      <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border">
        <h3 className="text-sm font-semibold text-ink">Comparaison scénarios</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper-warm text-ink-faint">✕</button>
      </div>
      <div className="overflow-x-auto p-5">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-ink-border">
              <th className="text-left py-2 text-ink-muted">Métrique</th>
              {scenarios.slice(0, 3).map((s, i) => <th key={i} className="text-right py-2 text-ink font-semibold">{s.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Budget', key: 'budget_new', fmt: (v: number) => fmtEur.format(v) },
              { label: 'Capture', key: 'expected_capture', fmt: (v: number) => fmtPct.format(v) },
              { label: 'Clics', key: 'expected_clicks', fmt: (v: number) => fmtNum.format(v) },
              { label: 'Conversions', key: 'expected_conversions', fmt: (v: number) => fmtDec.format(v) },
              { label: 'Revenu', key: 'expected_revenue', fmt: (v: number | null) => v != null ? fmtEur.format(v) : '—' },
            ].map((row) => {
              const values = scenarios.slice(0, 3).map((s) => (s.result as unknown as Record<string, number>)[row.key])
              const best = Math.max(...values.filter((v) => v != null))
              return (
                <tr key={row.label} className="border-b border-ink-border/30">
                  <td className="py-2 text-ink-muted">{row.label}</td>
                  {values.map((v, i) => (
                    <td key={i} className={cn('py-2 text-right font-medium', v === best && 'text-emerald-600 bg-emerald-50/50')}>
                      {row.fmt(v)}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────

export function BudgetSimulatorPage() {
  const { id: marketId } = useParams<{ id: string }>()
  const { data: market } = useMarket(marketId!)
  const { data: reco } = useBudgetReco(marketId!)
  const simulate = useSimulateBudget()

  const [mode, setMode] = useState<'budget' | 'target'>('budget')
  const [budgetVal, setBudgetVal] = useState(0)
  const [targetVal, setTargetVal] = useState(0.30)
  const [result, setResult] = useState<SimulateResponse | null>(null)
  const [showSave, setShowSave] = useState(false)
  const [showCompare, setShowCompare] = useState(false)
  const [scenarios, setScenarios] = useState<Scenario[]>(() => loadScenarios(marketId!))

  // Custom assumptions overrides
  const [overrides, setOverrides] = useState<Record<string, number>>({})
  const baseAssumptions = result?.assumptions ?? reco?.assumptions ?? {}

  useEffect(() => { if (reco?.budget_reco && budgetVal === 0) setBudgetVal(Math.round(reco.budget_reco)) }, [reco])

  const runSim = useCallback(() => {
    const p = mode === 'budget' ? { marketId: marketId!, budget_new: budgetVal } : { marketId: marketId!, target_capture: targetVal }
    simulate.mutate(p, { onSuccess: (d) => setResult(d as SimulateResponse) })
  }, [marketId, mode, budgetVal, targetVal])

  useEffect(() => {
    if ((mode === 'budget' && budgetVal <= 0) || (mode === 'target' && targetVal <= 0)) return
    const t = setTimeout(runSim, 300); return () => clearTimeout(t)
  }, [budgetVal, targetVal, mode, runSim])

  // Budget curve with both linear and elasticity lines
  const curveData = (() => {
    if (!result) return []
    const cb = result.current_budget; const cc = result.current_capture; const ea = result.e_adj ?? 0.4
    const pts = []
    for (let b = Math.max(500, cb * 0.3); b <= Math.min(MAX_BUDGET, cb * 3.5); b += cb * 0.08) {
      const ratio = b / cb
      const capElast = Math.min(0.70, cc * Math.pow(ratio, ea))
      const capLinear = Math.min(0.70, cc * ratio)
      pts.push({ budget: Math.round(b), elasticity: parseFloat((capElast * 100).toFixed(1)), linear: parseFloat((capLinear * 100).toFixed(1)) })
    }
    return pts
  })()

  const isLinear = reco?.method === 'linear'

  const handleSaveScenario = (name: string) => {
    if (!result) return
    const newScenario: Scenario = { name, budget: budgetVal, mode, result, assumptions: overrides }
    const updated = [...scenarios, newScenario].slice(-5)
    setScenarios(updated)
    saveScenarios(marketId!, updated)
  }

  const handleEditAssumption = (key: string, value: number) => {
    setOverrides((p) => ({ ...p, [key]: value }))
  }

  const handleResetAssumption = (key: string) => {
    setOverrides((p) => { const n = { ...p }; delete n[key]; return n })
  }

  return (
    <MarketTabsLayout>
      {/* Page-specific action bar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-ink">Simulateur budget</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold',
            isLinear ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-emerald-100 text-emerald-700 border border-emerald-200')}>
            <Zap size={12} />{isLinear ? 'Reco linéaire' : 'Modèle élasticité'}
          </span>
          {scenarios.length >= 2 && (
            <button onClick={() => setShowCompare(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink bg-paper-warm hover:bg-paper-warm rounded-lg">
              <GitCompare size={14} /> Comparer ({scenarios.length})
            </button>
          )}
          <button onClick={() => setShowSave(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brick bg-gold-light hover:bg-gold-light rounded-lg">
            <Save size={14} /> Sauvegarder
          </button>
          <button className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink bg-paper-warm hover:bg-paper-warm rounded-lg">
            <Download size={14} /> Exporter PDF
          </button>
        </div>
      </div>

      {/* Warnings */}
      {result?.warnings?.map((w, i) => (
        <div key={i} className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-card px-4 py-2.5">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" /><p className="text-sm text-amber-800">{w}</p>
        </div>
      ))}

      {/* KPI Strip */}
      {result && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Budget actuel" value={fmtEur.format(result.current_budget)} icon={DollarSign} color="text-ink bg-paper"
            drillDown={{ title: 'Budget actuel — 30 derniers jours' }} />
          <KpiCard label="Budget simulé" value={fmtEur.format(result.budget_new)} icon={Target} color="text-brick bg-gold-light" />
          <KpiCard label="Capture attendue" value={fmtPct.format(result.expected_capture)} icon={TrendingUp} color="text-semantic-capture bg-emerald-50"
            drillDown={{ title: 'Capture attendue', subtitle: `Linéaire: ${fmtPct.format(result.budget_linear / result.current_budget * result.current_capture)} vs Élasticité: ${fmtPct.format(result.expected_capture)}` }} />
          <KpiCard label="Confiance" value={reco ? `${reco.confidence_score.toFixed(0)}/100` : '—'} icon={Info} color="text-ink bg-gold-light" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* Mode Toggle + Controls */}
          <div className="bg-white rounded-card border border-ink-border p-5">
            <div className="flex items-center gap-2 mb-4">
              {(['budget', 'target'] as const).map((m) => (
                <button key={m} onClick={() => setMode(m)} className={cn('px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  mode === m ? 'bg-brick text-white' : 'bg-paper-warm text-ink hover:bg-paper-warm')}>
                  {m === 'budget' ? 'Je fixe mon budget' : 'Je fixe ma cible'}
                </button>
              ))}
            </div>
            {mode === 'budget' ? (
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-ink">Budget mensuel</span>
                  <input type="number" value={budgetVal} onChange={(e) => setBudgetVal(Number(e.target.value))}
                    className="w-28 text-sm text-right font-bold text-brick border border-ink-border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <input type="range" min={500} max={MAX_BUDGET} step={100} value={budgetVal} onChange={(e) => setBudgetVal(Number(e.target.value))}
                  className="w-full h-2 bg-paper-warm rounded-lg appearance-none cursor-pointer accent-gold" />
                <div className="flex justify-between text-[10px] text-ink-faint mt-1"><span>500 €</span><span>{fmtEur.format(MAX_BUDGET)}</span></div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between mb-2"><span className="text-sm text-ink">Objectif capture</span><span className="text-sm font-bold text-brick">{fmtPct.format(targetVal)}</span></div>
                <input type="range" min={0.05} max={0.70} step={0.01} value={targetVal} onChange={(e) => setTargetVal(Number(e.target.value))}
                  className="w-full h-2 bg-paper-warm rounded-lg appearance-none cursor-pointer accent-gold" />
              </div>
            )}
          </div>

          {/* Dual Curve Chart */}
          {curveData.length > 0 && (
            <div className="bg-white rounded-card border border-ink-border p-5" id="budget-printable-zone">
              <h3 className="text-sm font-semibold text-ink mb-4">Courbe budget → capture</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={curveData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="budget" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis unit="%" tick={{ fontSize: 11, fill: '#94a3b8' }} domain={[0, 75]} />
                  <Tooltip formatter={(v: number, name: string) => [`${v.toFixed(1)}%`, name === 'elasticity' ? 'Élasticité' : 'Linéaire']}
                    labelFormatter={(v) => fmtEur.format(v as number)} contentStyle={{ borderRadius: '0.75rem', border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <ReferenceArea y1={70} y2={75} fill="#fee2e2" fillOpacity={0.3} label={{ value: 'Zone saturation', fontSize: 9, fill: '#922B21' }} />
                  <Line type="monotone" dataKey="linear" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name="Linéaire" />
                  <Line type="monotone" dataKey="elasticity" stroke="#335cfc" strokeWidth={2} dot={false} name="Élasticité" />
                  {result && (
                    <>
                      <ReferenceLine x={Math.round(result.current_budget)} stroke="#94a3b8" strokeDasharray="5 5" label={{ value: '← Actuel', fontSize: 10, fill: '#94a3b8' }} />
                      <ReferenceDot x={Math.round(result.budget_new)} y={parseFloat((result.expected_capture * 100).toFixed(1))} r={6} fill="#335cfc" stroke="#fff" strokeWidth={2} />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Output Cards */}
          {result && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Clics attendus', value: fmtNum.format(result.expected_clicks), icon: MousePointerClick },
                { label: 'Conversions', value: fmtDec.format(result.expected_conversions), icon: Users },
                { label: 'Conv. incrémentales', value: `+${fmtDec.format(result.incremental_conversions)}`, icon: TrendingUp },
                { label: 'Revenu estimé', value: result.expected_revenue != null ? fmtEur.format(result.expected_revenue) : '—', icon: DollarSign },
              ].map((c) => (
                <div key={c.label} className="bg-white rounded-card border border-ink-border p-4">
                  {simulate.isPending ? <div className="h-12 bg-paper-warm rounded animate-pulse" /> : (
                    <><c.icon size={16} className="text-ink-faint mb-2" /><p className="text-lg font-bold text-ink">{c.value}</p><p className="text-[11px] text-ink-muted">{c.label}</p></>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assumptions Panel — ALWAYS visible, EDITABLE */}
        <div className="bg-white rounded-card border border-ink-border p-5 h-fit sticky top-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-ink">Hypothèses</h3>
            {Object.keys(overrides).length > 0 && (
              <button onClick={() => setOverrides({})} className="text-[10px] text-ink-faint hover:text-ink flex items-center gap-1">
                <RotateCcw size={10} /> Réinitialiser tout
              </button>
            )}
          </div>
          <div className="divide-y divide-ink-border/30">
            {[
              { key: 'ctr', label: 'CTR', fmt: (v: number) => `${(v * 100).toFixed(1)}%`, tooltip: 'Click-through rate des 30 derniers jours' },
              { key: 'cvr', label: 'CVR', fmt: (v: number) => `${(v * 100).toFixed(1)}%`, tooltip: 'Conversion rate des 30 derniers jours' },
              { key: 'effective_cpc', label: 'CPC effectif', fmt: (v: number) => `${v.toFixed(2)} €`, tooltip: 'Coût par clic moyen' },
              { key: 'e_adj', label: 'Élasticité', fmt: (v: number) => v.toFixed(4), tooltip: 'e_adj = e_raw × max(0.20, 1 − capture/0.70)' },
            ].map(({ key, label, fmt, tooltip }) => {
              const baseVal = (baseAssumptions as Record<string, number>)[key] ?? (result as Record<string, unknown> | null)?.[key] ?? 0
              const currentVal = overrides[key] ?? baseVal
              return (
                <EditableValue key={key} label={label} value={fmt(currentVal as number)} isModified={key in overrides}
                  onEdit={(v) => handleEditAssumption(key, v)} onReset={() => handleResetAssumption(key)} tooltip={tooltip} />
              )
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-ink-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-ink-muted">Méthode</span>
              <span className={cn('font-semibold', isLinear ? 'text-amber-600' : 'text-emerald-600')}>{result?.method ?? reco?.method ?? '—'}</span>
            </div>
          </div>
          <p className="text-[10px] text-ink-faint leading-relaxed mt-3">
            Dérivées des 30 derniers jours. Plafond capture 70%, multiplicateur max 3×.
          </p>
        </div>
      </div>

      <SaveScenarioModal open={showSave} onClose={() => setShowSave(false)} onSave={handleSaveScenario} />
      {showCompare && <ScenarioComparison scenarios={scenarios} onClose={() => setShowCompare(false)} />}
    </MarketTabsLayout>
  )
}
